/**
 * ServiceGroupWizard — 3-step modal for creating a service group.
 *
 * Step 1: Type + name + description
 * Step 2: Add services (search existing or mark for creation)
 * Step 3: Summary + confirm
 */
import React, { useEffect, useState, useCallback } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ServicePicker from './ServicePicker';

const STEP_LABELS = ['Tipo y nombre', 'Servicios', 'Confirmar'];

export default function ServiceGroupWizard({ companyId, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Meta
  const [meta, setMeta] = useState({ group_types: [] });

  // Step 1 form
  const [form, setForm] = useState({
    group_type: 'stack', icon: '', name: '', description: '',
  });

  // Step 2 — selected members
  const [members, setMembers] = useState([]);  // [{service_id, service_name, role}]

  useEffect(() => {
    if (!companyId) return;
    api.getServiceGroupsMeta(companyId).catch(() => ({})).then((m) => {
      if (m.group_types) setMeta(m);
    });
  }, [companyId]);

  const selectedType = meta.group_types.find(t => t.value === form.group_type);
  const displayIcon = form.icon || selectedType?.icon || '🔷';

  const addMember = (svc) => {
    setMembers(prev => {
      if (prev.find(m => m.service_id === svc.id)) return prev;
      return [...prev, { service_id: svc.id, service_name: svc.display_name || svc.name, role: '' }];
    });
  };

  const removeMember = (svcId) => {
    setMembers(prev => prev.filter(m => m.service_id !== svcId));
  };

  const updateRole = (svcId, role) => {
    setMembers(prev => prev.map(m => m.service_id === svcId ? { ...m, role } : m));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        group_type: form.group_type,
        icon: form.icon.trim() || null,
        members: members.map((m, i) => ({ service_id: m.service_id, role: m.role || null, sort_order: i })),
      };
      const res = await api.createServiceGroup(payload, companyId);
      onCreated?.(res.group);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const canNext = step === 1 ? form.name.trim().length > 0 : true;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.55)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 12, width: 560, maxWidth: '95vw', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: '1.3rem' }}>{displayIcon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Crear grupo de servicios</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Paso {step} de 3 — {STEP_LABELS[step - 1]}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <XMarkIcon style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--border)' }}>
          <div style={{ height: '100%', width: `${(step / 3) * 100}%`, background: 'var(--accent)', transition: 'width 0.2s' }} />
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem', marginBottom: 12 }}>⚠ {error}</div>}

          {/* ── STEP 1: Type + Name ── */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Tipo de grupo</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {meta.group_types.map(t => (
                    <button key={t.value} type="button"
                      onClick={() => setForm(f => ({ ...f, group_type: t.value }))}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                        padding: '8px 4px', borderRadius: 7, cursor: 'pointer',
                        border: form.group_type === t.value ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: form.group_type === t.value ? 'var(--blue-50)' : 'var(--bg-toolbar)',
                        fontSize: '0.72rem', color: form.group_type === t.value ? 'var(--accent)' : 'var(--text-secondary)',
                        fontWeight: form.group_type === t.value ? 700 : 400,
                      }}>
                      <span style={{ fontSize: '1.3rem' }}>{t.icon}</span>
                      {t.label.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                  Nombre del grupo *
                </label>
                <input className="form-control" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={`p.ej. Stack Docker vm-app-01`}
                  autoFocus style={{ fontSize: '0.9rem' }} />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                  Descripción (opcional)
                </label>
                <input className="form-control" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Breve descripción del grupo..."
                  style={{ fontSize: '0.85rem' }} />
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                  Icono personalizado (emoji, opcional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input className="form-control" value={form.icon}
                    onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                    placeholder={selectedType?.icon || '🔷'}
                    style={{ width: 80, fontSize: '1.2rem', textAlign: 'center' }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Déjalo vacío para usar el icono por defecto del tipo
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 2: Add services ── */}
          {step === 2 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                  Buscar y añadir servicios
                </label>
                <ServicePicker
                  companyId={companyId}
                  excludeIds={members.map(m => m.service_id)}
                  onPick={addMember}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  💡 ¿No existe el servicio? Escribe su nombre y pulsa <strong>Crear</strong> para darlo de alta al instante.
                </div>
              </div>

              {/* Added members */}
              {members.length === 0 ? (
                <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Busca arriba para añadir servicios al grupo.
                  <br/><span style={{ fontSize: '0.75rem' }}>Puedes añadir más después de crear el grupo.</span>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    {members.length} servicio{members.length !== 1 ? 's' : ''} añadido{members.length !== 1 ? 's' : ''}
                  </div>
                  {members.map((m, i) => (
                    <div key={m.service_id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', borderRadius: 6, marginBottom: 4,
                      background: 'var(--bg-toolbar)', border: '1px solid var(--border)',
                      fontSize: '0.82rem',
                    }}>
                      <span style={{ flex: 1, fontWeight: 600 }}>{m.service_name}</span>
                      <input value={m.role} onChange={e => updateRole(m.service_id, e.target.value)}
                        placeholder="rol (motor, panel...)"
                        style={{
                          width: 140, padding: '2px 8px', fontSize: '0.72rem',
                          border: '1px solid var(--border)', borderRadius: 4,
                          background: 'var(--bg-input)', color: 'var(--text-primary)',
                        }} />
                      <button type="button" onClick={() => removeMember(m.service_id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1 }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── STEP 3: Summary ── */}
          {step === 3 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: 12, background: 'var(--bg-toolbar)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '2rem' }}>{displayIcon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{form.name}</div>
                  {form.description && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{form.description}</div>}
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {selectedType?.label} · {members.length} servicio{members.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {members.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  El grupo se creará vacío. Puedes añadir servicios después.
                </p>
              ) : (
                <div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Servicios del grupo:</div>
                  {members.map(m => (
                    <div key={m.service_id} style={{
                      display: 'flex', gap: 8, padding: '5px 10px', fontSize: '0.82rem',
                      borderBottom: '1px solid var(--border)',
                    }}>
                      <span style={{ fontWeight: 600, flex: 1 }}>{m.service_name}</span>
                      {m.role && <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{m.role}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
          <button type="button" onClick={step === 1 ? onClose : () => setStep(s => s - 1)}
            className="dv-btn dv-btn-secondary" style={{ fontSize: '0.82rem' }}>
            {step === 1 ? 'Cancelar' : '◀ Atrás'}
          </button>

          {step < 3 ? (
            <button type="button" onClick={() => setStep(s => s + 1)}
              disabled={!canNext}
              className="dv-btn dv-btn-primary" style={{ fontSize: '0.82rem' }}>
              Siguiente ▶
            </button>
          ) : (
            <button type="button" onClick={handleCreate} disabled={saving}
              className="dv-btn dv-btn-primary" style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              {saving ? 'Creando…' : <><CheckIcon style={{ width: 14, height: 14 }} /> Crear grupo</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
